'use client';

import { useMemo, useState } from 'react';
import { ListSection } from '@/components/shared/page-shell';
import { GROUP_CARD } from '@/components/balance-sheet/components/balance-sheet-section';
import { CollapsibleRows } from '@/components/ui/collapsible-rows';
import { REVIEW_EMPTY_LIST_RENDERS_NOTHING } from './review-empty-state';
import { ListRowRhythmProvider } from '@/components/shared/list-row';
import { ReviewRowMarksInline } from './review-row-marks';
import { DocumentRow } from '@/components/documents/one-list/document-row';
import { DocumentDrawer } from '@/components/documents/one-list/document-drawer';
import { isCoreDocumentType } from '@/components/documents/v2/utils';
import { getFileTypeSlug } from '@/lib/services/files/utils';
import { useReviewDocumentRecords } from './use-review-document-records';
import type { DocumentItem } from '@/components/documents/v2';

/** The loose file shape both review surfaces already hand to their documents block. */
export interface ReviewDocument {
  id: number;
  originalFilename?: string;
  displayName?: string;
  uploadedAt?: string;
  createdAt?: string;
  sizeBytes?: number;
  mimeType?: string;
  isCertifiedCopy?: boolean;
  fileType?: string;
  documentType?: string;
}

export interface CompactDocumentsReviewProps {
  documents: ReviewDocument[];
  /**
   * The matter these files belong to, so the drawer can read their record
   * (VALE-1250).
   *
   * Optional only because a surface may not have one to give; without it the
   * drawer draws what the review shape carries and its Note row is read-only,
   * which is honest rather than degraded. Both live review surfaces pass it —
   * the in-matter extract review has the matter it is reviewing, and matter
   * creation has the draft matter its uploads were filed against.
   */
  matterId?: number;
  onViewDocument: (documentId: number) => void;
  onDownloadDocument: (documentId: number) => void;
  onDeleteDocument?: (documentId: number) => void;
  onCertifiedCopyChange?: (documentId: number, isCertified: boolean) => void;
}

/**
 * Adapt one uploaded review file to the row's shape.
 *
 * Mirrors `toDocumentItem` (`components/matter-details/tabs/documents-bucketing.ts`)
 * field for field, differing only where the review's file genuinely differs:
 * it carries `sizeBytes` directly rather than `fileSizeKb`, and its upload time
 * is `uploadedAt`. The core/additional split is **not** re-derived — it goes
 * through the same `isCoreDocumentType(getFileTypeSlug(...))` pair, so a change
 * to what counts as a core document reaches this surface too.
 */
export function toReviewDocumentItem(doc: ReviewDocument): DocumentItem {
  const typeSlug = getFileTypeSlug(doc.documentType ?? doc.fileType ?? '');

  return {
    id: doc.id,
    name: doc.displayName || doc.originalFilename || 'Unnamed file',
    /*
      The extension, or nothing. It used to default to `'pdf'`, which is a guess
      presented as a fact — on a review surface that is exactly the kind of
      confident-but-wrong value the reader is here to catch.
    */
    fileType: doc.originalFilename?.split('.').pop()?.toLowerCase() ?? '',
    category: isCoreDocumentType(typeSlug) ? 'core' : 'additional',
    uploadedAt: doc.uploadedAt || doc.createdAt || '',
    sizeBytes: doc.sizeBytes ?? 0,
    isCertifiedCopy: doc.isCertifiedCopy || false,
    mimeType: doc.mimeType,
    hasThumbnail: true,
    typeSlug,
    originalFilename: doc.originalFilename
  };
}

/**
 * The uploaded-documents block of an AI-extract review, on the Documents tab's
 * own row (VALE-977).
 *
 * The card grid this replaces was the last block still drawing its own thing:
 * the tab moved to dense rows precisely because "single-line rows put roughly
 * twenty documents on a screen against the card grid's seven"
 * (`documents-one-list.tsx`), and a review listing every uploaded file is
 * exactly that problem. Reusing the row also brings its `⋯` menu, so viewing,
 * downloading and marking a certified copy are the same gestures here as on the
 * tab.
 */
export function CompactDocumentsReview({
  documents,
  matterId,
  onViewDocument,
  onDownloadDocument,
  onDeleteDocument,
  onCertifiedCopyChange
}: CompactDocumentsReviewProps) {
  /*
    Each file's own record, for the detail the review shape cannot carry — its
    size, MIME type, upload date and note (VALE-1250). `resolve` hands the item
    straight back when the record is not there, so the list renders identically
    while the query is in flight.
  */
  const records = useReviewDocumentRecords(matterId);
  const { resolve, onNotesChange } = records;

  /*
    The surface's own handler where it has one — matter creation tracks the flag
    in its local working copy and reverts it on failure — and the record's own
    writer otherwise. Not left absent: this list renders `Mark as certified
    copy` unconditionally, so a surface with no handler was already offering the
    reviewer a menu item that silently did nothing, and gating the drawer's row
    on the same missing handler would have made the two disagree about the same
    flag rather than agree that it cannot be changed (VALE-1250).
  */
  const certifiedCopyChange =
    onCertifiedCopyChange ?? records.onCertifiedCopyChange;

  const items = useMemo(
    () => documents.map((doc) => resolve(toReviewDocumentItem(doc))),
    [documents, resolve]
  );

  /*
    Clicking a row previews the document in the Documents tab's own drawer
    rather than throwing it at a new browser tab. Reviewing an extract means
    checking documents against the values pulled out of them, and losing the
    review to a tab switch each time is the friction that makes people stop
    checking. The new tab stays available for a file the drawer cannot render —
    that is what `onOpenInNewTab` is for.
  */
  /*
    **The id, not the item.** A note saved from the drawer invalidates the
    files list, so the item the drawer is showing is rebuilt — holding the
    object would leave the panel displaying the note the reader has just
    replaced. Re-read from `items` by id, exactly as the Documents tab's own
    list does (`documents-one-list.tsx`).
  */
  const [openDocId, setOpenDocId] = useState<number | null>(null);
  const openDoc = items.find((doc) => doc.id === openDocId) ?? null;

  /*
    **A review with no uploads renders no uploads block** (VALE-1737).

    This list is the one member of the set with nothing to keep: its heading
    carries no Add — files reach a review by being uploaded to it, not from a
    control in this strip — so once the empty tile goes there is only a named
    card over nothing left. The contacts and entitlements twins keep their
    heading precisely because theirs still offers something.

    After the hooks above, never before them.
  */
  if (items.length === 0 && REVIEW_EMPTY_LIST_RENDERS_NOTHING) return null;

  return (
    <>
      {/*
        **The documents block alone stays at the COMPACT rhythm** (VALE-1584).

        Its three siblings on this surface take the roomy one (VALE-1494), and
        this block took it with them — but a review's uploads are the one list
        here that is a manifest rather than a set of findings. Nothing in a file
        row is being proposed, nothing is marked, and a bulk extract puts thirty
        of them under the values the reviewer is actually here to check. At
        `py-3.5` plus an icon tile they pushed the assets off the screen.

        The rhythm is provider-scoped, so this is the whole of the change: every
        other consumer of `DocumentRow` — the Documents tab's own one-list above
        all — keeps the height it had, which is the containment the ticket asked
        for. The drawer was already on the app default and is unaffected.
      */}
      {/* Marks beside the row's text, not in a right-hand column
          (VALE-1737) — `review-row-marks.tsx` carries the reason. */}
      <ReviewRowMarksInline>
        <ListRowRhythmProvider rhythm="compact">
          {/*
          **The same card its three siblings sit on** (VALE-1617). This list had
          no card at all, so its grey header strip ran edge to edge on the page
          background while every other block on the review was a rounded panel.
          `GROUP_CARD`'s `overflow-hidden` is what clips the strip to the radius.
        */}
          <ListSection
            label="Uploaded Documents"
            className={GROUP_CARD}
            scrollRows
          >
            {
              /*
            Collapsed past a handful, expanding in place rather than scrolling
            (VALE-1407). A bulk extract puts thirty-plus rows here and floods
            the review, but this list IS the content rather than a step above an
            action, and `ListSection` already owns the scrolling when its parent
            bounds the height — a second scroll box inside it would be the wrong
            answer. "+N more documents" and a full expand is the right one.
          */
              <CollapsibleRows
                noun="document"
                onOpen="expand"
                rows={items.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    isCore={doc.category === 'core'}
                    onOpen={() => setOpenDocId(doc.id)}
                    onDownload={onDownloadDocument}
                    onDelete={onDeleteDocument}
                    onCertifiedCopyChange={certifiedCopyChange}
                  />
                ))}
              />
            }
          </ListSection>
        </ListRowRhythmProvider>
      </ReviewRowMarksInline>

      <DocumentDrawer
        doc={openDoc}
        isCore={openDoc?.category === 'core'}
        onOpenChange={(next) => {
          if (!next) setOpenDocId(null);
        }}
        onDownload={onDownloadDocument}
        onOpenInNewTab={onViewDocument}
        /*
          The same handler the row's own menu item runs, so the drawer's
          Certified copy row and the list cannot disagree about whether the flag
          can be changed while reviewing.
        */
        onCertifiedCopyChange={certifiedCopyChange}
        onNotesChange={onNotesChange}
      />
    </>
  );
}
